import * as React from "react";
import { Button, DataGrid, DateBox, RadioGroup, SelectBox, TagBox, TextBox, ValidationGroup, ValidationSummary, Validator } from "devextreme-react";
import { Column, Lookup, RequiredRule } from "devextreme-react/data-grid";
import { ValueChangedInfo } from "devextreme/ui/editor/editor";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router";
import { IApiUrls } from "../../models/IApiUrls";
import { IDocument } from "../../models/IDocument";
import { DefaultVisitatieRapport, IVisitatieRapport, IVisitatieRapportData } from "../../models/IVisitatieRapport";
import { IVisitatieRapportGemeente } from "../../models/IVisitatieRapportGemeente";
import { IVisitatieRapportVisitatieData } from "../../models/IVisitatieRapportVisitatieDatum";
import { IVisitaiteRapportOperationeleDoelstelling } from "../../models/VisitatieRapportOperationeleDoelstelling";
import DataGridArrayList from "../../components/DataGridArrayEdit";
import useContstanten from "../../Constanten";
import { useDataSourceOptions, useFetchData, useListSource, useListSourceOptions, useToken, useUrlListSource } from "../../components/hooks/DataSource";
import DirtyPrompt from "../../components/DirtyPrompt";
import FileBox from "../../components/FileBox";
import { IPageLoading } from "../../App";
import useAppRoutes from "../../AppRoutes";
import useNotification from "../../components/hooks/Notification";
import { IListsModel } from "../../models/IHomeModel";

interface IProps extends IPageLoading {
    apiUrl: string
    apiUrls: IApiUrls
    lists: IListsModel
}
interface IUrlParams {
    id: string;
}

const VisitatieRapport = ({ apiUrls, setPageLoading, lists }: IProps) => {

    const history = useHistory();
    const notify = useNotification();
    const routes = useAppRoutes();
    const params = useParams<IUrlParams>();
    //const refFileUpload = useRef<FileUploader>(null);
    //const refUploadBtn = useRef<TextBoxButton>(null);
    //const fetchData = useFetchData();
    const [data, setData] = useState<IVisitatieRapport>(DefaultVisitatieRapport());
    //const [newKey, setNewKey] = useState(0);
    const dsGem = useListSource(lists.gemeenten, "naam");
    const dsShm = useListSource(lists.shms, "naam");
    const opDoelFilter = useMemo(() => ["!", ["hoofdDoelstellingId", "=", null]], []);
    //const [, allDocs] = useUrlListSource<IDocument>(apiUrls.documenten);
    const dsOpDoel = useListSourceOptions(lists.doelstellingen, "naam", opDoelFilter); 
    const fetcher = useFetchData();
    const valGroup = useRef<ValidationGroup>(null);
    const vdRef = useRef<DataGrid<IVisitatieRapportVisitatieData, number>>(null);
    const odRef = useRef<DataGrid<IVisitaiteRapportOperationeleDoelstelling, number>>(null);
    //const dsVrOperDoel = useListSource(data.operationeleDoelstellingen);
    const [isDirty, setIsDirty] = useState(false);
    const [dbGemeenten, setDbGemeenten] = useState<IVisitatieRapportGemeente[]>([]);

    const token = useToken();

    //useEffect(() => {
    //    console.log("new token", token);
    //}, [token])

    //const token = useToken();
    //Set dirty flag on data changes
    useEffect(() => {
        //also executed on load! Will reset in next effect (data loading)
        data && setIsDirty(true);
    }, [data])

    const setDataState = useCallback((apidata: IVisitatieRapportData) => {
        //keep gemeenten data (so we can send existing ids back to server)
        setDbGemeenten(apidata.gemeenten);
        //Convert gemeenten
        setData({ ...apidata, gemeenten: apidata.gemeenten.map(gem => gem.gemeenteId) });
        setIsDirty(false); //(re)loaded
        setPageLoading(false);
    }, [setPageLoading])

    //load data. (only runs when url id changes)
    useEffect(() => {
        setIsDirty(false); //dirty flag false when loading with id 0
        if (+params.id !== data.id) {
            setDbGemeenten([]); //Reset gem data!
            console.log("loading visitaiterapport", params.id);
            setPageLoading(+params.id > 0);
            +params.id > 0 && fetcher(`${apiUrls.visitatieRapporten}/${params.id}`).then(setDataState);
        }
    }, [fetcher, params.id, setDataState, apiUrls.visitatieRapporten, setPageLoading, data.id])


    const shmFormatNaam = useCallback((data) => data && ((data?.nummer || '') + ' - ' + (data?.naam || '')), [])

    const { activiteiten, visitatieronden, operationeleDoelstellingScore, dateOptions } = useContstanten();

    //const addNewRowOnContentReady = useCallback((e: EventInfo<dxDataGrid>) => data.id === 0 && !e.component.hasEditData() && e.component.addRow(), [data.id])

    const naamChangedHandler     = useCallback((e: ValueChangedInfo) => setData(p => ({ ...p, naam: e.value })), [])
    const gemChangedHandler      = useCallback((e: ValueChangedInfo) => setData(p => ({ ...p, gemeenten: e.value })), [])
    const shmChangedHandler      = useCallback((e: ValueChangedInfo) => setData(p => ({ ...p, shmId: e.value })), [])
    const pubDateChangedHandler  = useCallback((e: ValueChangedInfo) => setData(p => ({ ...p, publicatieDatum: e.value })), [])
    const activityChangedHandler = useCallback((e: ValueChangedInfo) => setData(p => ({ ...p, activiteit: e.value })), [])
    const rondeChangedHandler    = useCallback((e: ValueChangedInfo) => setData(p => ({ ...p, visitatieRonde: e.value })), [])
    //unbound datagrid arrays needs manual updates in react state.
    const visitatieDataChangedHandler = useCallback((e: IVisitatieRapportVisitatieData[]) =>
        setData(p => ({ ...p, visitatieData: e })), [])

    const operatDoelDataChangedHandler = useCallback((e: IVisitaiteRapportOperationeleDoelstelling[]) =>
        setData(p => ({ ...p, operationeleDoelstellingen: e })), [])

    const dataGridIsValid = useCallback((dgRef: React.RefObject<DataGrid<any, number>>) => {
        if (+params.id === 0 && !dgRef.current!.instance.hasEditData())
            return false;
        dgRef.current!.instance.saveEditData();
        return !dgRef.current!.instance.hasEditData();
    }, [params.id])

    const setNewIdsInList = useCallback((items: { id: number }[]) =>
        items.forEach(item => item.id = (item.id < 0 && 0) || item.id)
    , [])

    const validate = useCallback((): boolean => {
        const isValid = valGroup.current!.instance.validate().isValid;
        //const visDataValid = dataGridIsValid(vdRef);
        const oprDoelValid = isValid && dataGridIsValid(odRef);

        return (isValid && oprDoelValid) || false;
    }, [dataGridIsValid])

    const sendData = useCallback((publish: boolean = false) => {
        //save datagrids in state and validate form
        if (!validate()) {
            //console.log("er zijn validatiefouten!");
            notify("warning", "Er zijn validatiefouten!");                
            return;
        }
        vdRef.current?.instance.saveEditData();
        setPageLoading(true);
        //Recreate dataobject for gemeenten.
        const gemeenten = data.gemeenten.map(gemeenteId => dbGemeenten.find(p => p.gemeenteId === gemeenteId) || { id: 0, gemeenteId });
        const newData: IVisitatieRapportData = {
            ...data,
            //only send docId. Otherwise EF will try to add the existing record which results in a duplicate key error.
            documentId: data.document?.id || 0,
            document: undefined,
            gemeenten,
            gepubliceerd: data.gepubliceerd || publish
        };
        //reset ids to 0 (insert) when id < 0
        setNewIdsInList(newData.operationeleDoelstellingen)
        setNewIdsInList(newData.visitatieData)

        //send data to API (add or update)
        console.log("send data", newData);
        var apiUrl = apiUrls.visitatieRapporten + (data.id !== 0 ? '/' + data.id : '');
        fetcher(apiUrl, newData, data.id === 0 ? 'POST' : 'PUT').then((resp: IVisitatieRapportData) => {
            console.log(resp)
            setDataState(resp);
            data.id !== resp.id && history.replace(generatePath(routes.visitatieRapportenEdit, { id: resp.id }));
            notify("success", "Opslaan geslaagd.", 5000);
        }).catch(() => {
            setPageLoading(false);
            notify("error", "Opslaan mislukt!");
        });
    }, [data, fetcher, setDataState, dbGemeenten, apiUrls.visitatieRapporten, setNewIdsInList, setPageLoading, validate, history, routes.visitatieRapportenEdit, notify])
    
    const onPublishHandler = useCallback(() => sendData(true), [sendData])
    const onSaveHandler = useCallback(() => sendData(), [sendData])

    const inputAttr = useMemo(() => ({
        naam: { id: 'naam' },
        gemeenten: { id: 'gemeenten' },
        shm: { id: 'shm' },
        doc: { id: 'vrdoc' }, 
        pubDate: { id: 'pubdate' },
    }), [])

    //const [token, settoken] = useState("");

    //useEffect(() => {
    //    authService.getAccessToken().then(d => settoken(d || ""))
    //}, [])
    const docChangedHandler = useCallback((doc?: IDocument[]) => {
        setData(prev => ({ ...prev, documentId: doc?.[0]?.id || 0, document: doc?.[0] }))
    }, [])

    const allowedFileExtensions = useMemo(() => ([".pdf"]), [])
    const makeDirty = useCallback(() => setIsDirty(true), [])
    const gemSearchExprHandler = useMemo(() => ["naam","postcode"] , [])
    //const newRowData = useCallback((e: NewRowInfo<{ id: number }>) => e.data = { id: 0 }, [])
    return <ValidationGroup ref={valGroup}>
        <DirtyPrompt when={isDirty} />
        <h2>{isDirty ? "*" : ""}Visitatierapport: {data.naam}</h2>

        <div className="form-group">
            <label htmlFor={inputAttr.naam.id}>Naam</label>
            <TextBox
               // label="Naam"
                inputAttr={inputAttr.naam}
                value={data.naam}
                placeholder="Naam van rapport..."
                onValueChanged={naamChangedHandler}
            //onValueChange={writeModel}
            >
                <Validator>
                    <RequiredRule message="Naam is verplicht." />
                </Validator>
            </TextBox>
        </div>

        <div className="form-group">
            <label htmlFor={inputAttr.gemeenten.id}>Gemeenten</label>
            <TagBox
                inputAttr={inputAttr.gemeenten}
                dataSource={dsGem}
                value={data.gemeenten}
                onValueChanged={gemChangedHandler}
                showSelectionControls
                //hideSelectedItems
                placeholder="Geeft één of meerdere gemeenten op..."
                hint="Geef één of meerdere gemeenten op."                
                displayExpr="naam"
                valueExpr="id"
                //applyValueMode="instantly"
                //displayValue="naam"
                searchExpr={gemSearchExprHandler }
                searchEnabled                
            >
                <Validator>
                    <RequiredRule message="Geef minstens één gemeente op." />
                </Validator>
            </TagBox>
        </div>

        <div className="form-group">
            <label htmlFor={inputAttr.shm.id} >SHM </label>
            <SelectBox
                inputAttr={inputAttr.shm}
                dataSource={dsShm}
                displayExpr={shmFormatNaam}
                //displayExpr={"naam"}
                placeholder="Naam van SHM..."
                valueExpr="id"
                searchEnabled
                searchMode="contains"
                //minSearchLength={2}
                searchExpr={['nummer', 'naam']}
                value={data.shmId}
                onValueChanged={shmChangedHandler}
            >
                <Validator>
                    <RequiredRule message="Shm is verplicht."/>
                </Validator>
            </SelectBox>
        </div>

        <div className={"form-group"}>
            <label htmlFor={inputAttr.doc.id}>Rapport</label>
            <FileBox<IDocument>
                apiUrl={apiUrls.documenten}
                value={data.document}
                token={token}
                allowedExtensions={allowedFileExtensions}
                valueChange={docChangedHandler}
                placeholder="PDF-document van visitatierapport..."
                inputAttr={inputAttr.doc}
                required
                requiredMessage="Visitatierapport is verplicht."                
            />
        </div>

        <div className="form-group">
            <label htmlFor={inputAttr.pubDate.id}>Publicatiedatum</label>
            <DateBox
                //value={data.publicatieDatum}
                inputAttr={inputAttr.pubDate}
                dateSerializationFormat="yyyy-MM-dd"
                displayFormat="dd/MM/yyyy"
                placeholder="dd/mm/yyyy"
                value={data.publicatieDatum}
                onValueChanged={pubDateChangedHandler}
                openOnFieldClick
            >
                <Validator>
                    <RequiredRule message="Publicatiedatum is verplicht." />
                </Validator>
            </DateBox>
        </div>

        <div className="form-group">
            <label>Visitatiedata</label>
            <DataGridArrayList<IVisitatieRapportVisitatieData>
                gridRef={vdRef}
                list={data.visitatieData}
                valueChanged={visitatieDataChangedHandler}
                onEditing={makeDirty }
            >
                <Column dataField="datum" dataType="date" editorOptions={dateOptions} format="dd MMM yyyy" >
                    <RequiredRule />
                </Column>
            </DataGridArrayList>
        </div>

        <div className="row" >
            <div className="col-md">
                <div className="form-group">
                    <label>Activiteit</label>
                    <RadioGroup items={activiteiten} value={data.activiteit} onValueChanged={activityChangedHandler} valueExpr="id" >
                        <Validator>
                            <RequiredRule message="Activiteit is verplicht." />
                        </Validator>
                    </RadioGroup>
                </div>
            </div>
            <div className="col-md">
                <div className="form-group">
                    <label>Visitatieronde</label>
                    <RadioGroup items={visitatieronden} value={data.visitatieRonde} onValueChanged={rondeChangedHandler} valueExpr="id" >
                        <Validator>
                            <RequiredRule message="Visitatieronde is verplicht." />
                        </Validator>
                    </RadioGroup>
                </div>
            </div>
        </div>

        <div className="form-group">
            <label>Operationele doelstellingen</label>
            <DataGridArrayList<IVisitaiteRapportOperationeleDoelstelling>
                gridRef={odRef}
                list={data.operationeleDoelstellingen}
                valueChanged={operatDoelDataChangedHandler}
                onEditing={makeDirty} >
                <Column dataField="operationeleDoelstellingId" caption="Operationele doelstelling">
                    <Lookup
                        dataSource={dsOpDoel}                        
                        valueExpr="id"
                        displayExpr="naam" />
                    <RequiredRule />
                </Column>

                <Column dataField="score" width={120}>
                    <Lookup
                        dataSource={operationeleDoelstellingScore}
                        valueExpr="id"
                        displayExpr="text" />
                    <RequiredRule />
                </Column>
                <Column dataField="paginaNr" dataType="number" width={100} >
                    <RequiredRule />
                </Column>

            </DataGridArrayList>
        </div>


        <div className="form-group">
            <ValidationSummary width="100%" />
        </div>
        <div className="form-group buttons">
            <Button text="Opslaan als concept" onClick={onSaveHandler} type="normal" visible={!data.gepubliceerd}/>
            <Button text="Publiceren" onClick={onPublishHandler} type="default"  />
        </div>

    </ValidationGroup>
}
export default VisitatieRapport;